import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  Chip,
  Grid,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Add, Edit, Delete, PersonAdd, AdminPanelSettings, Business, Store, ManageAccounts, Badge } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { enqueueSnackbar } from 'notistack';

interface User {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  storeId?: number;
  isActive: boolean;
  createdAt: string;
  lastLogin?: string;
}

const roleIcons: Record<string, React.ReactElement> = {
  admin: <AdminPanelSettings />,
  executive: <Business />,
  corporate: <Business />,
  franchise_owner: <Store />,
  store_manager: <Badge />,
};

const roleColors: Record<string, any> = {
  admin: { background: 'linear-gradient(135deg, #af2326, #c94144)', color: '#fff' },
  executive: { background: 'linear-gradient(135deg, #c94144, #e85d5d)', color: '#fff' },
  corporate: { background: 'rgba(175, 35, 38, 0.2)', color: '#c94144' },
  franchise_owner: { background: 'rgba(175, 35, 38, 0.15)', color: '#e85d5d' },
  store_manager: { background: 'rgba(255, 255, 255, 0.05)', color: '#b8b8b8' },
};

const UserManagement: React.FC = () => {
  const { user: currentUser, hasRole } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    role: 'corporate',
    storeId: '',
  });

  const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/auth/users`);
      setUsers(response.data.users);
    } catch (error) {
      enqueueSnackbar('Failed to fetch users', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (user?: User) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        email: user.email,
        password: '',
        firstName: user.firstName,
        lastName: user.lastName,
        role: user.role,
        storeId: user.storeId?.toString() || '',
      });
    } else {
      setEditingUser(null);
      setFormData({
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        role: 'corporate',
        storeId: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingUser(null);
  };

  const handleSubmit = async () => {
    try {
      if (editingUser) {
        const updateData: any = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          role: formData.role,
          storeId: formData.storeId ? parseInt(formData.storeId) : null,
        };

        if (formData.password) {
          updateData.password = formData.password;
        }

        await axios.put(`${API_BASE_URL}/auth/users/${editingUser.id}`, updateData);
        enqueueSnackbar('User updated successfully', { variant: 'success' });
      } else {
        await axios.post(`${API_BASE_URL}/auth/register`, {
          ...formData,
          storeId: formData.storeId ? parseInt(formData.storeId) : null,
        });
        enqueueSnackbar('User created successfully', { variant: 'success' });
      }

      handleCloseDialog();
      fetchUsers();
    } catch (error: any) {
      const message = error.response?.data?.error || 'Operation failed';
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  const handleDeleteUser = async (userId: number) => {
    if (window.confirm('Are you sure you want to deactivate this user?')) {
      try {
        await axios.delete(`${API_BASE_URL}/auth/users/${userId}`);
        enqueueSnackbar('User deactivated successfully', { variant: 'success' });
        fetchUsers();
      } catch (error: any) {
        const message = error.response?.data?.error || 'Failed to deactivate user';
        enqueueSnackbar(message, { variant: 'error' });
      }
    }
  };

  if (!hasRole(['admin', 'executive'])) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">You don't have permission to access user management.</Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress sx={{ color: '#af2326' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            letterSpacing: '-0.02em',
            color: '#ffffff',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <ManageAccounts sx={{ mr: 2, fontSize: 32, color: '#af2326' }} />
          User Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<PersonAdd />}
          onClick={() => handleOpenDialog()}
          sx={{
            background: 'linear-gradient(135deg, #af2326 0%, #c94144 100%)',
            color: '#fff',
            borderRadius: '12px',
            padding: '10px 24px',
            fontWeight: 600,
            boxShadow: '0 4px 20px rgba(175, 35, 38, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #8a1a1c 0%, #af2326 100%)',
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 30px rgba(175, 35, 38, 0.4)',
            },
          }}
        >
          Add New User
        </Button>
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          backgroundColor: 'rgba(28, 24, 25, 0.85)',
          backdropFilter: 'blur(16px)',
          border: '1px solid rgba(255, 255, 255, 0.06)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
          borderRadius: '20px',
          overflow: 'hidden',
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ borderBottom: '1px solid rgba(175, 35, 38, 0.2)' }}>
              <TableCell
                sx={{ color: '#b8b8b8', fontWeight: 600, fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}
              >
                Name
              </TableCell>
              <TableCell
                sx={{ color: '#b8b8b8', fontWeight: 600, fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}
              >
                Email
              </TableCell>
              <TableCell
                sx={{ color: '#b8b8b8', fontWeight: 600, fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}
              >
                Role
              </TableCell>
              <TableCell
                sx={{ color: '#b8b8b8', fontWeight: 600, fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}
              >
                Store ID
              </TableCell>
              <TableCell
                sx={{ color: '#b8b8b8', fontWeight: 600, fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}
              >
                Status
              </TableCell>
              <TableCell
                sx={{ color: '#b8b8b8', fontWeight: 600, fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}
              >
                Last Login
              </TableCell>
              <TableCell
                sx={{ color: '#b8b8b8', fontWeight: 600, fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow
                key={user.id}
                sx={{
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.03)' },
                  borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
                }}
              >
                <TableCell sx={{ color: '#ffffff', fontWeight: 500 }}>
                  {user.firstName} {user.lastName}
                </TableCell>
                <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>{user.email}</TableCell>
                <TableCell>
                  <Chip
                    icon={roleIcons[user.role]}
                    label={user.role.replace('_', ' ').toUpperCase()}
                    sx={{
                      ...roleColors[user.role],
                      borderRadius: '8px',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      height: '28px',
                    }}
                    size="small"
                  />
                </TableCell>
                <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>{user.storeId || '-'}</TableCell>
                <TableCell>
                  <Chip
                    label={user.isActive ? 'Active' : 'Inactive'}
                    sx={{
                      background: user.isActive ? 'rgba(16, 185, 129, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                      color: user.isActive ? '#10b981' : '#6b7280',
                      borderRadius: '8px',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      height: '28px',
                    }}
                    size="small"
                  />
                </TableCell>
                <TableCell sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.875rem' }}>
                  {user.lastLogin ? new Date(user.lastLogin).toLocaleString() : 'Never'}
                </TableCell>
                <TableCell>
                  <IconButton
                    onClick={() => handleOpenDialog(user)}
                    sx={{
                      color: '#af2326',
                      '&:hover': { backgroundColor: 'rgba(175, 35, 38, 0.1)' },
                    }}
                    disabled={user.id === currentUser?.id}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton
                    onClick={() => handleDeleteUser(user.id)}
                    sx={{
                      color: '#ef4444',
                      '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.1)' },
                    }}
                    disabled={user.id === currentUser?.id}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(28, 24, 25, 0.95)',
            backdropFilter: 'blur(16px)',
            border: '1px solid rgba(255, 255, 255, 0.06)',
            borderRadius: '20px',
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: 'transparent',
            color: '#ffffff',
            borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
            fontSize: '1.25rem',
            fontWeight: 600,
          }}
        >
          {editingUser ? 'Edit User' : 'Create New User'}
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: 'transparent', pt: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                margin="normal"
                required
                disabled={!!editingUser}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={editingUser ? 'New Password (leave blank to keep current)' : 'Password'}
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                margin="normal"
                required={!editingUser}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                select
                label="Role"
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                margin="normal"
                required
              >
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="executive">Executive</MenuItem>
                <MenuItem value="corporate">Corporate</MenuItem>
                <MenuItem value="franchise_owner">Franchise Owner</MenuItem>
                <MenuItem value="store_manager">Store Manager</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Store ID (optional)"
                type="number"
                value={formData.storeId}
                onChange={(e) => setFormData({ ...formData, storeId: e.target.value })}
                margin="normal"
                helperText="Required for franchise owners and store managers"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            backgroundColor: 'transparent',
            borderTop: '1px solid rgba(255, 255, 255, 0.06)',
            p: 2.5,
          }}
        >
          <Button
            onClick={handleCloseDialog}
            sx={{
              color: 'rgba(255, 255, 255, 0.6)',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #af2326 0%, #c94144 100%)',
              color: '#fff',
              borderRadius: '12px',
              padding: '8px 20px',
              fontWeight: 600,
              boxShadow: '0 2px 10px rgba(175, 35, 38, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #8a1a1c 0%, #af2326 100%)',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 20px rgba(175, 35, 38, 0.4)',
              },
            }}
          >
            {editingUser ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;
